/**
 * Loading Overlay Component
 * Displays a loading spinner with message
 */

import React from 'react';
import { Backdrop, CircularProgress, Typography, Box, LinearProgress, Button } from '@mui/material';
import { useBOM } from '../contexts/BOMContext';

const LoadingOverlay: React.FC = () => {
  const { loading, loadingMessage, loadingProgress, setLoading } = useBOM();

  return (
    <Backdrop
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
      open={loading}
      onClick={(e) => {
        // Allow closing by clicking outside if extraction is complete or error
        if (loadingProgress && (loadingProgress.progress >= 100 || loadingMessage?.includes('complete'))) {
          e.stopPropagation();
        }
      }}
    >
      <Box 
        sx={{ 
          textAlign: 'center', 
          maxWidth: 600, 
          width: '90%',
          bgcolor: 'background.paper',
          color: 'text.primary',
          p: 3,
          borderRadius: 2,
          position: 'relative',
          boxShadow: 24,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {loadingProgress && loadingProgress.progress >= 100 ? (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: '#4caf50' }}>
              ✓ {loadingProgress.message || 'Complete'}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setLoading(false);
              }}
            >
              Close
            </Button>
          </Box>
        ) : (
          <>
            <CircularProgress color="primary" size={60} />
            {loadingMessage && (
              <Typography variant="h6" sx={{ mt: 2, mb: 2 }} color="text.primary">
                {loadingMessage}
              </Typography>
            )}
            {loadingProgress && (
              <Box sx={{ mt: 2, textAlign: 'left' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.primary">
                    {loadingProgress.message}
                  </Typography>
                  <Typography variant="body2" color="primary" fontWeight="bold">
                    {loadingProgress.progress}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={loadingProgress.progress} 
                  sx={{ mb: 2, height: 10, borderRadius: 1 }}
                  color="primary"
                />
                {loadingProgress.logs && loadingProgress.logs.length > 0 && (
                  <Box 
                    sx={{ 
                      maxHeight: 200, 
                      overflow: 'auto', 
                      bgcolor: 'action.hover', 
                      p: 1.5, 
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      textAlign: 'left'
                    }}
                  >
                    {loadingProgress.logs.slice(-10).map((log, idx) => (
                      <Typography key={idx} variant="caption" display="block" sx={{ mb: 0.5 }} color="text.secondary">
                        {log}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Backdrop>
  );
};

export default LoadingOverlay;

